import { OperationsHomeDashboard } from '../OperationsHomeDashboard';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import type {
  HomePageKey,
  HomePageVisibility,
  OperationsHomeAccess,
} from '../model';
import type { OperationsHomePort } from '../port';

const fixtureSummary = {
  currentHeadcount: 7,
  businessProcesses: { openSteps: 12 },
};

export type HomePageProps = {
  port: OperationsHomePort;
  access: OperationsHomeAccess;
  onNavigate: (page: HomePageKey) => void;
  visiblePages: HomePageVisibility;
  fixtureMode: boolean;
};

export function HomePage({ onNavigate, visiblePages, port, access, fixtureMode }: HomePageProps) {
  const dashboard = useAsyncResource(() => fixtureMode
    ? Promise.resolve(fixtureSummary)
    : access.canReadReports ? port.loadSummary() : Promise.resolve(undefined), [port, fixtureMode, access.canReadReports]);
  const workerResource = useAsyncResource<number | undefined>(() => fixtureMode || access.canReadReports || !access.canAccessPeople
    ? Promise.resolve(undefined)
    : port.loadWorkerCount(), [port, fixtureMode, access.canReadReports, access.canAccessPeople]);
  const organizationResource = useAsyncResource<number | undefined>(() => fixtureMode
    ? Promise.resolve(7)
    : access.canReadStaffing ? port.loadOrganizationCount() : Promise.resolve(undefined), [port, fixtureMode, access.canReadStaffing]);
  const companyResource = useAsyncResource<number | undefined>(() => fixtureMode
    ? Promise.resolve(2)
    : access.canReadTenantSettings ? port.loadCompanyCount() : Promise.resolve(undefined), [port, fixtureMode, access.canReadTenantSettings]);
  const pendingResource = useAsyncResource<number | undefined>(() => fixtureMode || access.canReadReports || !access.canAccessInbox
    ? Promise.resolve(undefined)
    : port.loadPendingInboxCount(), [port, fixtureMode, access.canReadReports, access.canAccessInbox]);
  const summary = dashboard.data;
  const downloadReport = () => { if (!access.canExportReports) return; void port.downloadWorkerReport().then((blob) => { const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = `workers-${new Date().toISOString().slice(0, 10)}.csv`; anchor.click(); URL.revokeObjectURL(url); }); };
  return <OperationsHomeDashboard
    values={{
      headcount: access.canAccessPeople ? dashboard.loading || workerResource.loading ? '…' : String(summary?.currentHeadcount ?? workerResource.data ?? '—') : undefined,
      organizations: access.canReadStaffing ? organizationResource.loading ? '…' : String(organizationResource.data ?? '—') : undefined,
      companies: access.canReadTenantSettings ? companyResource.loading ? '…' : String(companyResource.data ?? '—') : undefined,
      pending: access.canAccessInbox ? access.canReadReports ? dashboard.loading ? '…' : String(summary?.businessProcesses.openSteps ?? '—') : pendingResource.loading ? '…' : String(pendingResource.data ?? '—') : undefined,
    }}
    canAccessInbox={access.canAccessInbox}
    visiblePages={visiblePages}
    onNavigate={onNavigate}
    onDownload={access.canExportReports ? downloadReport : undefined}
  />;
}
